import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/contexts/LanguageContext";
import { Star, Sparkles } from "lucide-react";
import { trackBookingStart } from "@/lib/analytics";
import { api } from "@/lib/api";

const LOGO = "https://customer-assets.emergentagent.com/job_accommodation-plus/artifacts/quheoms4_WhatsApp%20Image%202026-05-01%20at%2014.58.40.jpeg";
const DEFAULT_SIDE_IMG = "https://images.unsplash.com/photo-1692650679395-4e2cb1cdeb2d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";

const Hero = () => {
  const { t } = useLang();
  const [sideImg, setSideImg] = useState(DEFAULT_SIDE_IMG);

  useEffect(() => {
    let mounted = true;
    api.get("/site/content")
      .then((res) => {
        const url = res.data?.hero?.image;
        if (mounted && typeof url === "string" && url.trim()) setSideImg(url.trim());
      })
      .catch(() => { /* fall back to default */ });
    return () => { mounted = false; };
  }, []);

  return (
    <section
      id="home"
      className="relative min-h-[100svh] grid lg:grid-cols-12 bg-[#FDFBF7]"
      data-testid="hero-section"
    >
      {/* LEFT — Editorial cream panel with logo */}
      <div className="lg:col-span-7 flex items-center pt-32 pb-16 px-6 md:px-12 lg:px-20 relative grain-overlay">
        <div className="max-w-xl animate-fadeUp w-full">
          <p className="text-overline mb-7" data-testid="hero-overline">{t.hero.overline}</p>

          {/* Logo banner: intentional card so the JPEG background looks designed */}
          <div
            className="inline-flex items-center justify-center bg-[#FDFBF7] rounded-2xl px-6 py-4 mb-2 shadow-[0_2px_24px_-8px_rgba(30,58,95,0.18)] ring-1 ring-[#E8E2D9]"
            data-testid="hero-logo-frame"
          >
            <img
              src={LOGO}
              alt="B&B Emmanuel — Home Hospitality"
              className="w-56 md:w-72 lg:w-80 h-auto"
              style={{ mixBlendMode: "multiply", filter: "contrast(1.04)" }}
              data-testid="hero-logo"
            />
          </div>

          <div className="bb-divider mt-4 mb-7" />

          <p
            className="font-display italic text-[1.7rem] md:text-[2rem] lg:text-[2.25rem] text-[#1E3A5F] leading-[1.15] mb-7 max-w-lg"
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </p>

          {/* Live availability badge removed per owner request */}

          <div className="flex flex-wrap gap-3">
            <Button
              asChild
              data-testid="hero-book-btn"
              className="bg-[#AC5D45] hover:bg-[#8A4936] text-white rounded-full h-12 px-7 text-base"
            >
              <Link to="/booking" onClick={() => trackBookingStart()}>{t.hero.cta_book}</Link>
            </Button>
            <Button
              data-testid="hero-explore-btn"
              variant="outline"
              className="bg-transparent border-[#1E3A5F]/25 text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white hover:border-[#1E3A5F] rounded-full h-12 px-7 text-base"
              onClick={() => document.getElementById("rooms")?.scrollIntoView({ behavior: "smooth" })}
            >
              {t.hero.cta_explore}
            </Button>
          </div>

          {/* Direct-booking advantage strip */}
          <div className="flex items-center gap-2 mt-4 text-xs text-[#635F59]" data-testid="hero-direct-perk">
            <Sparkles className="w-3.5 h-3.5 text-[#E3A857]" />
            <span>{t.hero.live_book_direct}</span>
          </div>

          {/* Small trust strip */}
          <div className="flex items-center gap-6 mt-12 pt-7 border-t border-[#E8E2D9]">
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event("open-reviews-modal"))}
              className="flex items-center gap-2 group cursor-pointer hover:opacity-80 transition-opacity"
              aria-label="Vedi tutte le recensioni"
              data-testid="hero-rating-button"
            >
              <div className="flex text-[#E3A857]">
                {[0, 1, 2, 3, 4].map((s) => (
                  <Star key={s} className="w-3.5 h-3.5 fill-[#E3A857]" />
                ))}
              </div>
              <span className="text-sm font-semibold text-[#1E3A5F]">7,5</span>
              <span className="text-xs text-[#635F59] group-hover:underline">/ 182 {t.about.stat2_l.toLowerCase()}</span>
              <span className="text-xs text-[#AC5D45] font-semibold opacity-60 group-hover:opacity-100">→</span>
            </button>
            <div className="hidden sm:block w-px h-6 bg-[#E8E2D9]" />
            <div className="hidden sm:block text-xs text-[#635F59]">
              <span className="font-semibold text-[#1E3A5F]">IT · EN · DE · FR</span>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT — Full-bleed Reggio Calabria image with floating quote */}
      <div className="lg:col-span-5 relative min-h-[55svh] lg:min-h-screen">
        <img
          src={sideImg}
          alt="Costa di Reggio Calabria — vista dello Stretto di Messina dal B&B Emmanuel"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A5F]/30 via-transparent to-[#AC5D45]/15" />
        <div
          className="absolute bottom-6 left-6 right-6 lg:right-auto lg:max-w-[18rem] bg-white/95 backdrop-blur-md px-6 py-5 rounded-xl shadow-xl border border-white/60"
          data-testid="hero-quote"
        >
          <p className="font-display italic text-lg md:text-xl text-[#1E3A5F] leading-snug">
            “{t.hero.quote}”
          </p>
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#AC5D45] mt-2 font-semibold">
            {t.hero.quote_author}
          </p>
        </div>
      </div>
    </section>
  );
};

export default Hero;
